import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg, fmtDateTime } from "@/lib/residentApi";

const SAMPLE = (k) => `${process.env.REACT_APP_BACKEND_URL}/api/re/lesson-voice/sample/${encodeURIComponent(k)}`;

function VoiceCard({ v, current, onUse }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(SAMPLE(v.key), { headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` }, responseType: "blob" });
      setUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error("Couldn't record this sample. Try again in a minute."); } finally { setBusy(false); }
  };
  const id = v.key.replace(/\W+/g, "-");
  return (
    <div data-testid={`voice-card-${id}`} className={`p-5 border rounded-sm space-y-3 ${current ? "border-[#d4a017] bg-[#d4a017]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="font-semibold">{v.label}</div>
          <div className="text-xs text-white/60 mt-0.5">{v.note}</div>
        </div>
        {current && <span data-testid={`voice-current-${id}`} className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#d4a017]">In use</span>}
      </div>
      {url ? <audio data-testid={`voice-audio-${id}`} src={url} controls autoPlay className="w-full"
        onPlay={(e) => document.querySelectorAll("audio").forEach((a) => { if (a !== e.currentTarget) a.pause(); })} /> : (
        <button type="button" disabled={busy} onClick={load} data-testid={`voice-play-${id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/20 text-white/80 font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm hover:border-white/40 disabled:opacity-50">
          {busy ? <><Loader2 className="h-3 w-3 animate-spin" /> Recording sample…</> : <><Play className="h-3 w-3" /> Play sample</>}
        </button>
      )}
      {!current && (
        <button type="button" onClick={() => onUse(v.key)} data-testid={`voice-use-${id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#d4a017] text-[#d4a017] font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-[#d4a017]/10">
          <Check className="h-3 w-3" /> Use this voice
        </button>
      )}
    </div>
  );
}

function Credits({ c }) {
  if (!c) return null;
  if (!c.ok) return <p data-testid="voice-credits-error" className="text-amber-300 text-sm">{c.error}</p>;
  return (
    <div data-testid="voice-credits" className={`p-4 border rounded-sm text-sm ${c.low ? "border-amber-400/40 bg-amber-500/10" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/50">ElevenLabs credits this month</div>
      <div className={`mt-1 text-2xl font-semibold ${c.low ? "text-amber-300" : "text-emerald-300"}`} data-testid="voice-credits-left">{c.pct_left}% left</div>
      <div className="text-white/60 text-xs mt-1">{c.left.toLocaleString()} of {c.limit.toLocaleString()} characters · warning shows below {c.low_pct}%
        {c.resets_at ? ` · refills ${new Date(c.resets_at * 1000).toLocaleDateString()}` : ""}</div>
    </div>
  );
}

export default function LessonVoicePage() {
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const load = () => reApi.get("/lesson-voice").then(setS).catch((e) => setError(e?.response?.status === 403 ? "Super admin only." : errMsg(e)));
  useEffect(() => { load(); }, []);
  const use = async (voice) => {
    try { await reApi.put("/lesson-voice", { voice }); toast.success("Voice switched · lessons re-record in it the next time they play"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <main data-testid="lesson-voice-page" className="min-h-screen bg-[#0b0814] text-white px-6 py-10">
      <div className="max-w-5xl mx-auto space-y-8">
        <Link to="/admin" data-testid="lesson-voice-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#d4a017]"><ArrowLeft className="h-3.5 w-3.5" /> Admin OS</Link>
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]">System · Super Admin</div>
          <h1 className="text-4xl sm:text-5xl font-semibold mt-1">Lesson Voice</h1>
          <p className="text-white/60 mt-2 text-sm">Play the same lesson part in each voice, then pick the one that reads every homework lesson.</p>
        </div>
        {error && <p data-testid="lesson-voice-error" className="text-red-300">{error}</p>}
        {s && <>
          <Credits c={s.credits} />
          <div className="grid md:grid-cols-3 gap-4">
            {s.voices.map((v) => <VoiceCard key={v.key} v={v} current={v.key === s.current} onUse={use} />)}
          </div>
          {s.last_fallback_at && <p data-testid="voice-last-fallback" className="text-xs text-white/50">Backup voice last used {fmtDateTime(s.last_fallback_at)}</p>}
        </>}
      </div>
    </main>
  );
}
